import React, { useState, useEffect } from 'react';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../context/AlertContext';
import axios from '../api/axios';
import './OBSSync.css';

function OBSSync() {
  const socket = useSocket();
  const alert = useAlert();
  
  const [connected, setConnected] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [referenceSource, setReferenceSource] = useState('');
  const [targetSources, setTargetSources] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [syncOffsets, setSyncOffsets] = useState({});
  const [manualSource, setManualSource] = useState('');
  const [manualOffset, setManualOffset] = useState(0);

  useEffect(() => {
    loadStatus();
    loadOBSSources();

    if (socket) {
      socket.on('sync:status', handleSyncStatus);
      socket.on('sync:update', handleSyncUpdate);
      socket.on('sync:agentDisconnected', handleAgentDisconnected);

      return () => {
        socket.off('sync:status', handleSyncStatus);
        socket.off('sync:update', handleSyncUpdate);
        socket.off('sync:agentDisconnected', handleAgentDisconnected);
      };
    }
  }, [socket]);

  const loadStatus = async () => {
    try {
      const { data } = await axios.get('/api/obs-sync/status');
      setConnected(data.connected);
      if (data.status) {
        setSyncEnabled(data.status.enabled);
        setReferenceSource(data.status.reference || '');
        setTargetSources(data.status.targets || []);
        setSyncOffsets(data.status.offsets || {});
      }
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };

  const loadOBSSources = async () => {
    // Load available sources from OBS
    // This would need to be implemented in the OBS API
    setAvailableSources([
      'Handy-Cam',
      'Feste-Cam',
      'Cam3',
      'Desktop',
      'Game'
    ]);
  };

  const handleSyncStatus = (data) => {
    console.log('[Sync Status]', data);
    setConnected(true);
    setSyncEnabled(data.enabled);
    setReferenceSource(data.reference || '');
    setTargetSources(data.targets || []);
    setSyncOffsets(data.offsets || {});
  };

  const handleSyncUpdate = (data) => {
    console.log('[Sync Update]', data);
    setSyncOffsets(prev => ({
      ...prev,
      [data.source]: data.offset
    }));
    
    if (data.manual) {
      alert.success(`Manual offset ${data.offset}ms applied to ${data.source}`);
    } else {
      alert.info(`Auto-sync: ${data.delay}ms delay detected on ${data.source}`);
    }
  };

  const handleAgentDisconnected = () => {
    setConnected(false);
    setSyncEnabled(false);
    alert.warning('Sync Agent disconnected');
  };

  const handleStartSync = async () => {
    if (!referenceSource || targetSources.length === 0) {
      alert.error('Please select reference and target sources');
      return;
    }

    try {
      await axios.post('/api/obs-sync/start', {
        reference: referenceSource,
        targets: targetSources
      });
      alert.success('Sync started');
    } catch (error) {
      alert.error('Failed to start sync: ' + error.message);
    }
  };

  const handleStopSync = async () => {
    try {
      await axios.post('/api/obs-sync/stop');
      alert.success('Sync stopped');
    } catch (error) {
      alert.error('Failed to stop sync: ' + error.message);
    }
  };

  const handleManualSync = async () => {
    if (!manualSource) {
      alert.error('Please select a source');
      return;
    }

    try {
      await axios.post('/api/obs-sync/manual', {
        source: manualSource,
        offset: parseInt(manualOffset)
      });
    } catch (error) {
      alert.error('Failed to apply manual sync: ' + error.message);
    }
  };

  const toggleTargetSource = (source) => {
    setTargetSources(prev => {
      if (prev.includes(source)) {
        return prev.filter(s => s !== source);
      } else {
        return [...prev, source];
      }
    });
  };

  return (
    <div className="obs-sync-page">
      <div className="page-header">
        <h2>🔄 OBS Stream Sync</h2>
        <p>Automatische Synchronisation von SRT/SRTLA Streams</p>
        <div className="connection-status">
          {connected ? (
            <span className="status-badge connected">✅ Agent verbunden</span>
          ) : (
            <span className="status-badge disconnected">❌ Agent nicht verbunden</span>
          )}
          {syncEnabled && (
            <span className="status-badge syncing">🔄 Sync aktiv</span>
          )}
        </div>
      </div>

      {!connected && (
        <div className="warning-box">
          <h3>⚠️ Sync Agent nicht verbunden</h3>
          <p>Der OBS Sync Agent muss auf dem Streaming-Server laufen.</p>
          <p>Siehe <code>obs-sync-agent/README.md</code> für Installation.</p>
        </div>
      )}

      <div className="sync-grid">
        {/* Auto Sync Settings */}
        <div className="sync-card">
          <h3>⚙️ Automatische Synchronisation</h3>
          
          <div className="form-group">
            <label>Referenz-Quelle (Basis für Sync)</label>
            <select 
              value={referenceSource} 
              onChange={(e) => setReferenceSource(e.target.value)}
              disabled={syncEnabled}
            >
              <option value="">Wähle Referenz-Quelle...</option>
              {availableSources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
            <small>Die Quelle die als Zeitbasis dient (z.B. Handy-Cam)</small>
          </div>

          <div className="form-group">
            <label>Zu synchronisierende Quellen</label>
            <div className="source-checkboxes">
              {availableSources
                .filter(s => s !== referenceSource)
                .map(source => (
                  <label key={source} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={targetSources.includes(source)}
                      onChange={() => toggleTargetSource(source)}
                      disabled={syncEnabled}
                    />
                    <span>{source}</span>
                  </label>
                ))}
            </div>
            <small>Quellen die an die Referenz angepasst werden</small>
          </div>

          <div className="button-group">
            {!syncEnabled ? (
              <button 
                className="btn btn-primary"
                onClick={handleStartSync}
                disabled={!connected || !referenceSource || targetSources.length === 0}
              >
                ▶️ Sync starten
              </button>
            ) : (
              <button 
                className="btn btn-danger"
                onClick={handleStopSync}
              >
                ⏹️ Sync stoppen
              </button>
            )}
          </div>
        </div>

        {/* Manual Sync */}
        <div className="sync-card">
          <h3>🎛️ Manueller Sync Offset</h3>
          
          <div className="form-group">
            <label>Quelle</label>
            <select 
              value={manualSource} 
              onChange={(e) => setManualSource(e.target.value)}
            >
              <option value="">Wähle Quelle...</option>
              {availableSources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Offset (Millisekunden)</label>
            <input
              type="number"
              value={manualOffset}
              onChange={(e) => setManualOffset(e.target.value)}
              step="100"
              placeholder="z.B. 500"
            />
            <small>
              Positiv = Quelle verzögern | Negativ = Quelle beschleunigen
            </small>
          </div>

          <button 
            className="btn btn-secondary"
            onClick={handleManualSync}
            disabled={!connected || !manualSource}
          >
            ✓ Offset anwenden
          </button>
        </div>

        {/* Current Offsets */}
        <div className="sync-card full-width">
          <h3>📊 Aktuelle Sync-Offsets</h3>
          
          {Object.keys(syncOffsets).length === 0 ? (
            <p className="no-data">Keine Offsets aktiv</p>
          ) : (
            <div className="offsets-grid">
              {Object.entries(syncOffsets).map(([source, offset]) => (
                <div key={source} className="offset-item">
                  <div className="offset-source">{source}</div>
                  <div className={`offset-value ${offset > 0 ? 'positive' : offset < 0 ? 'negative' : 'zero'}`}>
                    {offset > 0 ? '+' : ''}{offset}ms
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>ℹ️ Wie funktioniert es?</h4>
        <ul>
          <li><strong>Automatischer Sync:</strong> Misst kontinuierlich Delays und passt Offsets an</li>
          <li><strong>Manueller Sync:</strong> Setze feste Offsets für einzelne Quellen</li>
          <li><strong>Referenz-Quelle:</strong> Die Basis-Quelle (meist die stabilste Kamera)</li>
          <li><strong>Target-Quellen:</strong> Werden an die Referenz angepasst</li>
          <li><strong>Sync-Interval:</strong> Prüfung alle 5 Sekunden (konfigurierbar im Agent)</li>
        </ul>
      </div>
    </div>
  );
}

export default OBSSync;
